import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminTheme } from '../context/AdminThemeContext';
import { Save, ArrowLeft, Lock, Loader2 } from 'lucide-react';
import Notification from '../../Component/Notification'; // Adjust path

const EditStudent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { colors } = useAdminTheme();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Notification State
  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    googleId: "",
    mobile: ""
  });

  useEffect(() => {
    const fetchUser = async () => {
        try {
            // const res = await fetch(`http://localhost:2025/api/admin/students/${id}`);
            const res = await fetch(`https://www.projectinfotech.in/api/admin/students/${id}`);
            if(res.ok) {
                const data = await res.json();
                setFormData({
                    name: data.name || "",
                    email: data.email || "",
                    googleId: data.googleId || "",
                    mobile: data.mobile || ""
                });
            } else {
                setNotify({ isOpen: true, message: 'User not found', type: 'error' });
            }
        } catch(err) {
            setNotify({ isOpen: true, message: 'Failed to fetch user details', type: 'error' });
        } finally {
            setLoading(false);
        }
    };
    fetchUser();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
        // const res = await fetch(`http://localhost:2025/api/admin/students/${id}`, {
        const res = await fetch(`https://www.projectinfotech.in/api/admin/students/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
        });
        
        if(res.ok) {
            setNotify({ isOpen: true, message: 'User updated successfully!', type: 'success' });
            // Slight delay before redirect so user can see the success popup
            setTimeout(() => {
                navigate('/admin/students');
            }, 1500);
        } else {
            setNotify({ isOpen: true, message: 'Failed to update user', type: 'error' });
        }
    } catch(err) {
        setNotify({ isOpen: true, message: 'Server Error during update', type: 'error' });
    } finally {
        setSaving(false);
    }
  };

  if(loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-red-600"/></div>;

  return (
    <div className="animate-fade-in-up max-w-2xl mx-auto">
      <Notification 
        isOpen={notify.isOpen} 
        message={notify.message} 
        type={notify.type} 
        onClose={() => setNotify({ ...notify, isOpen: false })} 
      />

      <button 
        onClick={() => navigate('/admin/students')} 
        className={`mb-6 flex items-center gap-2 text-sm font-bold opacity-70 hover:opacity-100 ${colors.navbarText}`}
      >
        <ArrowLeft size={16} /> Back to List
      </button>

      <div className={`p-8 rounded-xl shadow-lg border ${colors.cardBg} ${colors.cardText} ${colors.borderColor}`}>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
           Edit User <span className="text-sm font-normal opacity-50">(ID: {id})</span>
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Read Only Fields */}
          <div className="grid grid-cols-2 gap-4">
             <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-2 opacity-60">Email Address</label>
                <div className="relative">
                    <input 
                      type="text" value={formData.email} disabled 
                      className="w-full p-3 rounded-lg border bg-gray-100/50 text-gray-500 cursor-not-allowed border-gray-200"
                    />
                    <Lock className="absolute right-3 top-3 text-gray-400" size={16} />
                </div>
             </div>
             <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-2 opacity-60">Google ID</label>
                <div className="relative">
                    <input 
                      type="text" value={formData.googleId || "Not Linked"} disabled 
                      className="w-full p-3 rounded-lg border bg-gray-100/50 text-gray-500 cursor-not-allowed border-gray-200"
                    />
                    <Lock className="absolute right-3 top-3 text-gray-400" size={16} />
                </div>
             </div>
          </div>

          <div className="border-t border-dashed opacity-30 my-4"></div>

          {/* Editable Fields */}
          <div>
            <label className="block text-sm font-semibold mb-2">Full Name</label>
            <input 
              type="text" 
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className={`w-full p-3 rounded-lg border focus:ring-2 focus:outline-none ${colors.inputBg} ${colors.borderColor}`}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Mobile Number</label>
            <input 
              type="tel" 
              value={formData.mobile}
              onChange={e => setFormData({...formData, mobile: e.target.value})}
              className={`w-full p-3 rounded-lg border focus:ring-2 focus:outline-none ${colors.inputBg} ${colors.borderColor}`}
            />
          </div>

          <button 
            type="submit" 
            disabled={saving}
            className={`w-full py-3 rounded-lg font-bold text-white shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 ${colors.button3D}`}
          >
            {saving ? <Loader2 className="animate-spin" /> : <Save size={18} />} Save Changes
          </button>

        </form>
      </div>
    </div>
  );
};

export default EditStudent;